import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getAppInfo } from 'app/store/Electron/selectors';
import { config } from 'config';
import { channels } from 'shared/constants';
import { ElectronAppInfo } from 'types/general.types';
import { setAppInfoAction } from 'app/store/Electron/slice';
import { DeeplinkActions } from 'types/enums';
import {
  getFormattedKeySignature,
  getFormattedTimeSignature,
  openURL,
  parseJSON,
} from 'app/utils/utils';
import { handleCreateSongWithDeeplinkingEffect } from 'app/store/SongList/effects';
import { routes } from 'app/routing/routesConfig';
import { DAWMetadata } from './types';

export function useElectron() {
  const dispatch = useAppDispatch();
  const appInfo = useAppSelector(getAppInfo);
  const { api } = window;

  useEffect(() => {
    if (config.isElectronBuild && !appInfo) {
      api.invoke(channels.APP_INFO).then((info: ElectronAppInfo) => {
        dispatch(setAppInfoAction(info));
      });
    }
  }, [appInfo, api, dispatch]);
}

export function useDeeplinkAction() {
  const { api } = window;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (config.isElectronBuild) {
      const removeListener = api.on(
        channels.EXECUTE_ACTION,
        async (action: string, meta?: string) => {
          if (action === DeeplinkActions.ADD_SONG) {
            const metadata: DAWMetadata = parseJSON(meta);
            try {
              const { guid } = await dispatch(
                handleCreateSongWithDeeplinkingEffect({
                  data: {
                    title: metadata?.metadata?.projectName,
                    location: metadata?.metadata?.location,
                    creationDate: metadata?.heuristicMetadata?.dateCreated,
                    duration: metadata?.metadata?.durationSecs,
                    tempo: metadata?.metadata?.tempo,
                    keySignature: getFormattedKeySignature(
                      metadata?.metadata?.keySignature
                    ),
                    timeSignature: getFormattedTimeSignature(
                      metadata?.metadata?.timeSignature
                    ),
                  },
                  isFromProTools: true,
                })
              ).unwrap();
              if (guid) {
                navigate(routes.songAudioList.path.replace(':guid', guid), {
                  replace: true,
                  state: {
                    shouldNotFetch: true,
                  },
                });
              }
            } catch (e: any) {
              navigate(routes.auth.path, {
                replace: true,
                state: {
                  from: routes.songs.path,
                  action: {
                    type: DeeplinkActions.ADD_SONG,
                    metadata: e?.data,
                  },
                },
              });
            }
          }
        }
      );
      return () => {
        removeListener();
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useDeeplinking() {
  const { api } = window;
  const navigate = useNavigate();

  useEffect(() => {
    if (config.isElectronBuild) {
      api.on(channels.REDIRECT, (path: string) => {
        navigate(path);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useElectronForceUpdate() {
  const location = useLocation();
  const [forceUpdateReady, setForceUpdateReady] = useState(false);
  const [forceUpdateTriggered, setForceUpdateTriggered] = useState(false);
  const { api } = window;

  useEffect(() => {
    if (config.isElectronBuild) {
      api.on(channels.SET_FORCE_UPDATE_READY, () => {
        setForceUpdateReady(true);
      });
      api.on(channels.SET_FORCE_UPDATE_NOT_READY, () => {
        setForceUpdateReady(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (forceUpdateReady && config.isElectronBuild && !forceUpdateTriggered) {
      setForceUpdateTriggered(true);
      window.api.send(channels.TRIGGER_FORCE_UPDATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, forceUpdateTriggered]);
}

export function useLinkMiddleMouseHandler() {
  const auxclickHandler = (e) => {
    if (e.button === 1) {
      const clickedLink = e.path.find((el) => el.localName === 'a');
      if (clickedLink) {
        openURL(clickedLink.hash.slice(1), { newTab: true });
        e.preventDefault();
      }
    }
  };

  useEffect(() => {
    if (config.isElectronBuild) {
      document.addEventListener('auxclick', auxclickHandler, false);
    }
    return () => {
      if (config.isElectronBuild) {
        document.removeEventListener('auxclick', auxclickHandler);
      }
    };
  }, []);
}
